class InventoryCountdownCart {
  constructor() {
    this.init();
  }

  init() {
    // Initialize both subscription and one-time buttons
    document.querySelectorAll('.inventory-countdown__add-to-cart').forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
    
    document.querySelectorAll('.inventory-countdown__add-onetime').forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
    
    // Initialize time countdown buttons
    document.querySelectorAll('.time-countdown__add-to-cart').forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
    
    document.querySelectorAll('.time-countdown__add-onetime').forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
    
    // Initialize banner button add-to-cart
    document.querySelectorAll('.banner__add-to-cart').forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });

    // Initialize floating image clickable elements
    document.querySelectorAll('.banner__floating-image--clickable').forEach(element => {
      element.addEventListener('click', this.handleAddToCart.bind(this));
      // Also support keyboard interaction
      element.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleAddToCart.call(this, e);
        }
      });
    });
  }

  async checkVariantAvailability(variantId) {
    try {
      // Check if variantId is valid
      if (!variantId || variantId.trim() === '') {
        return false;
      }
      
      const variantResponse = await fetch(`/variants/${variantId}.js`);
      if (!variantResponse.ok) {
        return false;
      }
      
      const variantData = await variantResponse.json();
      
      // Return true if variant is available
      return variantData.available === true;
    } catch (error) {
      // If we can't check, assume it's not available to be safe
      return false;
    }
  }

  async handleAddToCart(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const subscriptionVariantId = button.dataset.subscriptionVariantId;
    const onetimeVariantId = button.dataset.onetimeVariantId;
    const purchaseType = button.dataset.purchaseType;
    const sellingPlanId = button.dataset.sellingPlanId;
    const useTwoProducts = button.dataset.useTwoProducts === 'true';

    // Check if we're using the new two-product system (time countdown or inventory fallback)
    // At least one variant ID should be present and non-empty in two-product mode
    const hasSubscriptionId = subscriptionVariantId && subscriptionVariantId.trim() !== '';
    const hasOnetimeId = onetimeVariantId && onetimeVariantId.trim() !== '';
    const isTwoProductMode = hasSubscriptionId || hasOnetimeId;

    if (!isTwoProductMode && !variantId) {
      console.error('No variant ID found');
      return;
    }

    // Disable button and show loading state
    this.setLoadingState(button, true);

    try {
      let items;
      
      if (isTwoProductMode) {
        // Two-product mode: add both products based on purchase type
        let subscriptionPlanId = null;
        
        // Get selling plan if adding as subscription.
        // Use the merchant-configured selling plan id setting ONLY;
        // no first-available array-index fallback.
        if (purchaseType === 'subscription') {
          if (sellingPlanId && sellingPlanId.trim() !== '') {
            subscriptionPlanId = parseInt(sellingPlanId);
          }

          if (!subscriptionPlanId) {
            throw new Error('No subscription plan configured for this product');
          }
        }
        
        items = await this.buildTwoProductCartItems(subscriptionVariantId, onetimeVariantId, subscriptionPlanId, purchaseType);
      } else {
        // Single product mode (legacy behavior)
        let planId = null;
        
        // Get selling plan for subscriptions.
        // Use the merchant-configured selling plan id setting ONLY;
        // no first-available array-index fallback.
        if (purchaseType === 'subscription') {
          if (sellingPlanId && sellingPlanId.trim() !== '') {
            planId = parseInt(sellingPlanId);
          }

          if (!planId) {
            throw new Error('No subscription plan configured for this product');
          }
        }
        
        items = this.buildCartItems(variantId, planId);
      }
      
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(items)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const data = await response.json();
      
      // Show success feedback
      this.showSuccessMessage(button);
      
      // Small delay to ensure server has processed the cart update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Update cart UI - trigger Shopify's cart update event
      await this.updateCart();
      
      // Dispatch custom event for other components (like fixed CTA bar)
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage(button, error.message || 'Unable to add to cart');
      
      // Dispatch error event for other components (like fixed CTA bar)
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { error } }));
    } finally {
      // Re-enable button
      setTimeout(() => {
        this.setLoadingState(button, false);
      }, 1500);
    }
  }

  buildCartItems(variantId, sellingPlanId) {
    const item = {
      id: variantId,
      quantity: 1,
      properties: {
      }
    };

    // Add selling plan for subscriptions
    if (sellingPlanId) {
      item.selling_plan = sellingPlanId;
    }

    return { items: [item] };
  }

  async buildTwoProductCartItems(subscriptionVariantId, onetimeVariantId, sellingPlanId, purchaseType) {
    // Build array with both products, checking availability first
    const items = [];
    
    // Check if variant IDs are valid and check availability
    const hasSubscriptionVariant = subscriptionVariantId && subscriptionVariantId.trim() !== '';
    const hasOnetimeVariant = onetimeVariantId && onetimeVariantId.trim() !== '';
    
    const subscriptionAvailable = hasSubscriptionVariant ? await this.checkVariantAvailability(subscriptionVariantId) : false;
    const onetimeAvailable = hasOnetimeVariant ? await this.checkVariantAvailability(onetimeVariantId) : false;
    
    if (purchaseType === 'subscription') {
      // Both products as subscription - only add if available
      if (subscriptionAvailable) {
        items.push({
          id: subscriptionVariantId,
          quantity: 1,
          selling_plan: sellingPlanId,
          properties: {
          }
        });
      }
      if (onetimeAvailable) {
        items.push({
          id: onetimeVariantId,
          quantity: 1,
          selling_plan: sellingPlanId,
          properties: {
          }
        });
      }
    } else {
      // Both products as one-time purchases - only add if available
      if (subscriptionAvailable) {
        items.push({
          id: subscriptionVariantId,
          quantity: 1,
          properties: {
          }
        });
      }
      if (onetimeAvailable) {
        items.push({
          id: onetimeVariantId,
          quantity: 1,
          properties: {
          }
        });
      }
    }
    
    // If no items are available, throw an error
    if (items.length === 0) {
      throw new Error('Products are currently out of stock');
    }

    return { items: items };
  }

  setLoadingState(element, isLoading) {
    // Check if this is a floating image
    const isFloatingImage = element.classList.contains('banner__floating-image--clickable');
    
    if (isFloatingImage) {
      // For floating images, just change opacity and add a loading class
      if (isLoading) {
        element.style.opacity = '0.5';
        element.style.pointerEvents = 'none';
        element.classList.add('loading');
      } else {
        element.style.opacity = '1';
        element.style.pointerEvents = 'auto';
        element.classList.remove('loading');
      }
    } else {
      // Handle both inventory countdown buttons and fixed CTA bar buttons
      const buttonText = element.querySelector('.inventory-countdown__button-text, .fixed-cta-bar__button-text, .fh-banner-btn-text');
      const loadingSpinner = element.querySelector('.inventory-countdown__button-loading, .fixed-cta-bar__button-loading, .fh-banner-btn-loading');

      if (isLoading) {
        element.disabled = true;
        element.style.opacity = '0.7';
        if (buttonText) buttonText.style.display = 'none';
        if (loadingSpinner) loadingSpinner.style.display = 'inline-block';
      } else {
        element.disabled = false;
        element.style.opacity = '1';
        if (buttonText) buttonText.style.display = 'inline';
        if (loadingSpinner) loadingSpinner.style.display = 'none';
      }
    }
  }

  showSuccessMessage(element) {
    // Check if this is a floating image
    const isFloatingImage = element.classList.contains('banner__floating-image--clickable');
    
    if (isFloatingImage) {
      // For floating images, add a visual success effect
      const originalTransform = element.style.transform;
      element.style.transform = 'scale(1.1)';
      element.style.transition = 'transform 0.3s ease';
      
      // Add a success class for CSS styling
      element.classList.add('added-to-cart');
      
      setTimeout(() => {
        element.style.transform = originalTransform;
        element.classList.remove('added-to-cart');
      }, 600);
    } else {
      const buttonText = element.querySelector('.inventory-countdown__button-text, .fixed-cta-bar__button-text, .fh-banner-btn-text');
      if (!buttonText) return;

      const originalText = buttonText.textContent;

      buttonText.textContent = '✓ Added to Cart!';
      element.style.background = 'linear-gradient(180deg, #28a745 0%, #218838 100%)';
      
      setTimeout(() => {
        buttonText.textContent = originalText;
        element.style.background = '';
      }, 2000);
    }
  }

  showErrorMessage(element, message) {
    // Check if this is a floating image
    const isFloatingImage = element.classList.contains('banner__floating-image--clickable');
    
    if (isFloatingImage) {
      // For floating images, add a visual error effect
      const originalTransform = element.style.transform;
      element.style.transform = 'translateX(-5px)';
      element.style.transition = 'transform 0.1s ease';
      
      setTimeout(() => {
        element.style.transform = 'translateX(5px)';
      }, 100);
      
      setTimeout(() => {
        element.style.transform = 'translateX(-5px)';
      }, 200);
      
      setTimeout(() => {
        element.style.transform = originalTransform;
      }, 300);
      
      // Optionally show a toast or alert
      console.error('Add to cart error:', message);
    } else {
      const buttonText = element.querySelector('.inventory-countdown__button-text, .fixed-cta-bar__button-text, .fh-banner-btn-text');
      if (!buttonText) return;

      const originalText = buttonText.textContent;

      buttonText.textContent = '✗ ' + message;
      element.style.background = 'linear-gradient(180deg, #dc3545 0%, #c82333 100%)';
      
      setTimeout(() => {
        buttonText.textContent = originalText;
        element.style.background = '';
      }, 3000);
    }
  }

  async updateCart(retryCount = 0) {
    try {
      console.log('Updating cart drawer... (attempt ' + (retryCount + 1) + ')');
      
      // Fetch the cart drawer and icon bubble sections
      const response = await fetch('/?sections=cart-drawer,cart-icon-bubble');
      const sections = await response.json();
      
      console.log('Cart sections fetched:', sections);
      
      // Check if cart sections still show empty cart (might be a race condition)
      if (sections['cart-drawer'] && sections['cart-drawer'].includes('is-empty') && retryCount < 2) {
        console.warn('Cart sections still show empty cart, retrying in 200ms...');
        await new Promise(resolve => setTimeout(resolve, 200));
        return this.updateCart(retryCount + 1);
      }
      
      // Get the cart drawer element
      const cartDrawer = document.querySelector('cart-drawer');
      
      if (!cartDrawer) {
        console.warn('Cart drawer element not found');
        return;
      }
      
      // CRITICAL: Remove is-empty class from both cart-drawer element and inner elements
      // This prevents trapFocus from looking for .drawer__inner-empty which doesn't exist after adding items
      if (cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
        console.log('Removed is-empty class from cart-drawer element');
      }
      
      const drawerInner = cartDrawer.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.classList.contains('is-empty')) {
        drawerInner.classList.remove('is-empty');
        console.log('Removed is-empty class from drawer__inner');
      }
      
      if (sections) {
        // Set up a temporary MutationObserver to prevent is-empty class from being added back
        let isRemoving = false; // Flag to prevent infinite loop
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class' && !isRemoving) {
              if (cartDrawer.classList.contains('is-empty')) {
                console.warn('[MutationObserver] is-empty class was added back, removing it!');
                isRemoving = true;
                cartDrawer.classList.remove('is-empty');
                setTimeout(() => { isRemoving = false; }, 50);
              }
            }
          });
        });
        
        observer.observe(cartDrawer, { 
          attributes: true, 
          attributeFilter: ['class'] 
        });
        
        // Stop observing after 1 second
        setTimeout(() => observer.disconnect(), 1000);
        
        // Try using the cart drawer's built-in renderContents method
        if (typeof cartDrawer.renderContents === 'function') {
          try {
            console.log('Using cart drawer renderContents method');
            cartDrawer.renderContents({ sections: sections });
            return; // Success, exit here
          } catch (renderError) {
            console.warn('renderContents failed, falling back to manual update:', renderError);
            observer.disconnect(); // Stop observing if there's an error
          }
        }
        
        // Manual update as fallback
        console.log('Using manual cart update');
        this.manualUpdateCart(sections, cartDrawer);
        observer.disconnect(); // Stop observing after manual update
      }
      
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  manualUpdateCart(sections, cartDrawer) {
    try {
      // Ensure is-empty class is removed (double-check)
      if (cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
        console.log('[Manual Update] Removed is-empty class from cart-drawer');
      }
      
      // Update cart icon bubble first
      if (sections['cart-icon-bubble']) {
        const cartIconBubble = document.querySelector('#cart-icon-bubble');
        if (cartIconBubble) {
          const parser = new DOMParser();
          const bubbleDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          const newBubble = bubbleDoc.querySelector('.shopify-section');
          if (newBubble) {
            cartIconBubble.innerHTML = newBubble.innerHTML;
            console.log('Cart icon updated');
          }
        }
      }

      // Update cart drawer content
      if (sections['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
        
        // Update the entire CartDrawer element to preserve structure
        const newCartDrawer = doc.querySelector('#CartDrawer');
        const currentCartDrawer = document.querySelector('#CartDrawer');
        
        if (newCartDrawer && currentCartDrawer) {
          currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
          console.log('Cart drawer content updated');
          
          // Make absolutely sure is-empty is not on the updated inner content
          const updatedInner = cartDrawer.querySelector('.drawer__inner');
          if (updatedInner && updatedInner.classList.contains('is-empty')) {
            updatedInner.classList.remove('is-empty');
            console.log('[Manual Update] Removed is-empty from updated drawer__inner');
          }
          
          // Small delay to ensure DOM is ready before setting up handlers
          setTimeout(() => {
            // Re-setup overlay click handler
            const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
            if (overlay) {
              // Remove old listeners by cloning
              const newOverlay = overlay.cloneNode(true);
              overlay.parentNode.replaceChild(newOverlay, overlay);
              
              // Add new listener
              newOverlay.addEventListener('click', () => {
                console.log('Overlay clicked, closing drawer');
                if (cartDrawer && typeof cartDrawer.close === 'function') {
                  cartDrawer.close();
                }
              });
              console.log('Overlay click handler re-attached');
            }
            
            // Ensure close button works
            const closeButton = cartDrawer.querySelector('.drawer__close');
            if (closeButton) {
              closeButton.addEventListener('click', () => {
                console.log('Close button clicked');
                if (cartDrawer && typeof cartDrawer.close === 'function') {
                  cartDrawer.close();
                }
              });
              console.log('Close button handler attached');
            }
            
            // Re-initialize cart drawer's subscription buttons if the method exists
            if (typeof cartDrawer.setupSubscriptionButtons === 'function') {
              cartDrawer.setupSubscriptionButtons();
              console.log('Cart drawer subscription buttons re-initialized');
            }
            
            // Open the cart drawer
            setTimeout(() => {
              if (cartDrawer && typeof cartDrawer.open === 'function') {
                cartDrawer.open();
                console.log('Cart drawer opened');
              }
            }, 50);
          }, 100);
        }
      }
      
    } catch (error) {
      console.error('Error in manual cart update:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InventoryCountdownCart();
  });
} else {
  new InventoryCountdownCart();
}

// Re-initialize on Shopify section re-render
document.addEventListener('shopify:section:load', () => {
  new InventoryCountdownCart();
});

